import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import {
  sidebarPanelContentAtom,
  sidebarPanelFeatureEnabledAtom,
} from '@/atoms/sidebarPanelAtoms';
import { topStripCountAtom } from '@/atoms/top-strip-atoms';
import { LowDiskAlert } from '@/components/low-disk-alert';
import { ConnectivityAlert } from '@/components/offline-recording/connectivity-alert';
import { NoteFailedWarning } from '@/components/offline-recording/note-failed-warning';
import {
  useIsAgentHomeV1Enabled,
  useIsFlagProviderSettled,
  useIsNewSessionListEnabled,
  useIsRibbonSidebarEnabled,
} from '@/features/flags';
import {
  IS_INSET_DESIGN_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/features/flags/legacy-flags';
import { useHiddenNavInSmartOnFhirLaunch } from '@/hooks/integrations/useHiddenNavInSmartOnFhirLaunch';
import { cn } from '@/lib/cn';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { PageShell } from '@heidi/design-system/page-shell';
import { ResizablePanel, ResizablePanelGroup } from '@heidi/design-system/resizable';
import SkipLink, { skipLinkTargetProps } from '@heidi/design-system/skip-link';
import { FullPageLoader } from '@heidi/snowflake-ui/full-page-loader';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMenuCoachmark } from './coachmark';
import { EvidenceSidebarPanel } from './evidence-sidebar-panel';
import { useCollapseSidebarOnHealthDirectLaunch } from './hooks/useCollapseSidebarOnHealthDirectLaunch';
import { ShellContentElementContext } from './hooks/useShellContentElement';
import { NavigationContentBoundary } from './navigation-content-boundary';
import { OtherSidebarPanels } from './OtherSidebarPanels';
import { ResizableMenuPanels } from './ResizableMenuPanels';
import { ScribeNavigation } from './scribe-navigation';
import { SessionsListPanelFixed } from './sessions-list-panel-fixed';

interface ResizableLayoutProps {
  children: React.ReactNode;
  shouldShowDesktopSidebar: boolean;
}

/**
 * Desktop Layout Component
 * Handles the desktop layout with resizable panels wrapped in PageShell.
 *
 * On in-session routes:
 *   SidebarPanel OR Sessions list (fixed, left) + Main Content + Evidence sidebar (resizable, right)
 * Otherwise:
 *   Old single-tree layout via ResizableMenuPanels (unchanged)
 */
export const ResizableLayout = ({
  children,
  shouldShowDesktopSidebar,
}: ResizableLayoutProps) => {
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const isInsetDesignEnabled = IS_INSET_DESIGN_ENABLED() ?? false;
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  // Agent Home augments the existing primary sidebar with a secondary panel.
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  // A generic SMART App Launch keeps the Agent Home panel off: it is a
  // cross-patient surface. The flag itself stays as-is so the rest of the
  // Agent Home cohort's chrome (no Evidence/Work item, no chat sidebar)
  // is unchanged; only the panel mount point is suppressed.
  const hiddenNavInSmartLaunch = useHiddenNavInSmartOnFhirLaunch();
  const showAgentHomePanel =
    isAgentHomeV1Enabled && !hiddenNavInSmartLaunch.has('agentHome');
  // The sidebar-design flags (ribbon / sidebar-panel / inset) are read
  // synchronously from PostHog. On a cold load they evaluate to their `false`
  // defaults until PostHog resolves, then flip — which paints the old sidebar
  // first and swaps to the new one (a visible flicker). Hold the sidebar chrome
  // off the tree until flags are ready so it paints the correct design once.
  const flagsReady = usePosthogFlagsReady();
  // The new session list reads an OpenFeature flag; PostHog readiness alone
  // still permits a layout swap while that provider is mid-init. Also require
  // the flag provider to have settled (fail-open ERROR included) so the shell
  // paints once against the real flag values.
  const isFlagProviderSettled = useIsFlagProviderSettled();
  const navGateReady = flagsReady && isFlagProviderSettled;
  const setSidebarPanelContent = useSetAtom(sidebarPanelContentAtom);
  const setSidebarPanelFeatureEnabled = useSetAtom(sidebarPanelFeatureEnabledAtom);

  // Sync the flag value to a reactive atom so launchModalsConfigAtom
  // can subscribe and queue the coachmark immediately when this changes.
  useEffect(() => {
    setSidebarPanelFeatureEnabled(isSidebarPanelEnabled && !isAgentHomeV1Enabled);
  }, [isSidebarPanelEnabled, isAgentHomeV1Enabled, setSidebarPanelFeatureEnabled]);
  const router = useRouter();
  const isSessionRoute = router.pathname.startsWith('/scribe/session/');
  // The new sessions split view (/scribe/sessions[/id]) renders the list and
  // detail as two separate cards floating on the sidebar's `bg-surface-accent`
  // chrome — same pattern the EvidenceSidebarPanel uses on the right side.
  // Clear the SidebarInset's paint here so each panel can render its own card.
  const isSessionsSplitRoute = router.pathname.startsWith('/scribe/sessions');
  const hasTopStrip = useAtomValue(topStripCountAtom) > 0;
  // Coding-case detail pages are the same card-on-chrome shape: the canvas
  // card + cards-column aside are separate panels floating on the sidebar's
  // accent chrome (see packages/coding/src/ui/case/components/coding-case-page.tsx).
  const isCodingCaseRoute = router.pathname.startsWith('/coding-dashboard/cases');
  const isCardOnChromeRoute = isSessionsSplitRoute || isCodingCaseRoute;

  // Force the nav sidebar collapsed on every HealthDirect launch.
  useCollapseSidebarOnHealthDirectLaunch();

  // Auto-trigger menu coachmark flow when conditions are met
  useMenuCoachmark();

  // Hold the whole shell — nav included — behind a full-page loader until the
  // flag domain settles, so the session list never paints its content column
  // before the nav. Scoped to that route: the ~50 other desktop routes keep
  // painting immediately, and so does the legacy `/scribe/session/` detail
  // route, where a flag-OFF user is not in this migration and would otherwise
  // lose time-to-content it already has. A flag-ON user passing through the
  // legacy route sees a loader either way — that page renders its own while it
  // redirects. Mobile never enters: `shouldShowDesktopSidebar` is already
  // `&& !isMobile` at the call site.
  if (shouldShowDesktopSidebar && !navGateReady && isSessionsSplitRoute) {
    return <FullPageLoader />;
  }

  return (
    <PageShell defaultCollapsible={isRibbonSidebarEnabled ? 'label-icons' : 'icon'}>
      <SkipLink href="#content">
        <FormattedMessage defaultMessage="Skip to main content" id="2sRCsy/hKv" />
      </SkipLink>
      {isSidebarPanelEnabled && !isAgentHomeV1Enabled ? (
        <>
          <SkipLink
            href="#page-shell-panel"
            onClick={() => setSidebarPanelContent('sessions')}
          >
            <FormattedMessage defaultMessage="Skip to sessions panel" id="cHfq9WwCyf" />
          </SkipLink>
          <SkipLink
            href="#page-shell-panel"
            onClick={() => setSidebarPanelContent('evidence')}
          >
            <FormattedMessage defaultMessage="Skip to evidence panel" id="96zu4rg1dE" />
          </SkipLink>
        </>
      ) : (
        <SkipLink href="#sidebar-session-list-panel">
          <FormattedMessage
            defaultMessage="Skip to sessions list panel"
            id="zNUhJJc7uG"
          />
        </SkipLink>
      )}

      {flagsReady && (
        <ScribeNavigation
          // A strip above the navigation row (this route's banner, or an
          // app-wide one) would sit under a viewport-pinned rail.
          positioning={isSessionsSplitRoute || hasTopStrip ? 'shell' : 'viewport'}
          showPrimarySidebar={shouldShowDesktopSidebar}
          collapsible={isRibbonSidebarEnabled ? 'label-icons' : 'icon'}
          variant={isInsetDesignEnabled ? 'inset' : 'sidebar'}
          secondaryPanel={
            isAgentHomeV1Enabled
              ? showAgentHomePanel
                ? 'agent-home'
                : null
              : isSidebarPanelEnabled
                ? 'sidebar'
                : null
          }
        />
      )}

      {/* Status banners sit in the content column (right of the fixed left rail)
          so the rail never clips them, while they still span the full content
          width above every panel. Each banner owns its own visibility gates. */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <ConnectivityAlert />
        <NoteFailedWarning />
        {/* Low-disk warning for the active desktop capture. Same strip slot as
            the connectivity alert; self-gates on its flag + isTauri() and only
            renders while the warned capture remains active. */}
        <LowDiskAlert />
        {isSessionRoute ? (
          <DualPanelLayout
            isSidebarPanelEnabled={isSidebarPanelEnabled}
            isNewSessionListEnabled={isNewSessionListEnabled}
            isInsetDesign={isInsetDesignEnabled}
          >
            {children}
          </DualPanelLayout>
        ) : (
          <LegacyLayout
            isInsetDesign={isInsetDesignEnabled}
            isSessionsSplitRoute={isSessionsSplitRoute}
            isCardOnChromeRoute={isCardOnChromeRoute}
          >
            {children}
          </LegacyLayout>
        )}
      </div>
    </PageShell>
  );
};

const MainContentPanel = ({
  children,
  isInsetDesign,
  isSessionsSplitRoute = false,
  isCardOnChromeRoute = false,
  leading,
}: {
  children: React.ReactNode;
  isInsetDesign: boolean;
  isSessionsSplitRoute?: boolean;
  isCardOnChromeRoute?: boolean;
  leading?: React.ReactNode;
}) => {
  const isEvidenceSidebarOpen = useAtomValue(evidenceSidebarOpenAtom);
  const [shellContent, setShellContent] = useState<HTMLDivElement | null>(null);

  return (
    <ResizablePanel
      id="main-content"
      order={1}
      className={cn(
        isSessionsSplitRoute && 'min-h-0 min-w-0 overflow-hidden',
        leading ? 'flex flex-row' : 'flex flex-col'
      )}
    >
      {leading}
      <PageShell.Main
        className={cn(
          'p-0',
          !isInsetDesign && 'h-full',
          leading && 'flex-1',
          isEvidenceSidebarOpen && isInsetDesign && 'sm:me-2',
          // The sessions split view constrains its panels and hides overflow so
          // each resizable pane scrolls internally — tasks must NOT inherit this
          // (it stays content-height with page-level scroll).
          isSessionsSplitRoute && 'min-h-0 min-w-0 overflow-hidden',
          // Page paints its own panels on the sidebar-chrome background;
          // strip the SidebarInset's white bg and inset frame here so each
          // panel can render as a separate card matching the evidence-panel
          // pattern. Card surfaces are painted on the inner asides, not here.
          isCardOnChromeRoute && 'bg-transparent sm:m-0 sm:rounded-none'
        )}
      >
        <ShellContentElementContext.Provider value={shellContent}>
          <PageShell.Content
            ref={setShellContent}
            {...skipLinkTargetProps(
              'content',
              `relative flex-1 overflow-x-hidden p-0 @container ${
                isSessionsSplitRoute
                  ? 'min-w-0 min-h-0 overflow-hidden'
                  : isCardOnChromeRoute
                    ? ''
                    : isInsetDesign
                      ? 'bg-surface-shell'
                      : 'h-dvh bg-surface-page'
              }`
            )}
          >
            <NavigationContentBoundary>{children}</NavigationContentBoundary>
          </PageShell.Content>
        </ShellContentElementContext.Provider>
      </PageShell.Main>
    </ResizablePanel>
  );
};

const LegacyLayout = ({
  children,
  isInsetDesign,
  isSessionsSplitRoute,
  isCardOnChromeRoute,
}: {
  children: React.ReactNode;
  isInsetDesign: boolean;
  isSessionsSplitRoute: boolean;
  isCardOnChromeRoute: boolean;
}) => (
  <div
    id="layout-panel-group-container"
    className={cn(
      'flex border-collapse overflow-hidden',
      isSessionsSplitRoute ? 'min-h-0 min-w-0 flex-1' : 'flex-1'
    )}
  >
    <ResizablePanelGroup
      direction="horizontal"
      className={isSessionsSplitRoute ? 'h-auto min-h-0 min-w-0 flex-1' : 'flex-1'}
      // The session list's scrollbar sits on this seam; 16px would swallow half
      // of it on Windows.
      resizeTargetMinimumSize={{ coarse: 20, fine: 10 }}
    >
      <ResizableMenuPanels />
      <MainContentPanel
        isInsetDesign={isInsetDesign}
        isSessionsSplitRoute={isSessionsSplitRoute}
        isCardOnChromeRoute={isCardOnChromeRoute}
        leading={<OtherSidebarPanels />}
      >
        {children}
      </MainContentPanel>
    </ResizablePanelGroup>
  </div>
);

const DualPanelLayout = ({
  children,
  isSidebarPanelEnabled,
  isNewSessionListEnabled,
  isInsetDesign,
}: {
  children: React.ReactNode;
  isSidebarPanelEnabled: boolean;
  isNewSessionListEnabled: boolean;
  isInsetDesign: boolean;
}) => (
  <div className="flex flex-1 border-collapse overflow-hidden">
    {!isSidebarPanelEnabled && !isNewSessionListEnabled && <SessionsListPanelFixed />}
    <OtherSidebarPanels />
    <div
      id="layout-panel-group-container"
      className="flex flex-1 border-collapse overflow-hidden"
    >
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        <MainContentPanel isInsetDesign={isInsetDesign}>{children}</MainContentPanel>
        <EvidenceSidebarPanel />
      </ResizablePanelGroup>
    </div>
  </div>
);
